/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Edit dialog for bike bus. Only allows editing of group leader and completion status
as per requirements. Date and stops cannot be modified after creation.
*/

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { bikeBusService, BikeBus, BikeBusStatus } from "@/api/bikeBusService";

interface BikeBusEditDialogProps {
  bikeBus: BikeBus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const BikeBusEditDialog = ({ bikeBus, open, onOpenChange, onSuccess }: BikeBusEditDialogProps) => {
  const [leader, setLeader] = useState("");
  const [status, setStatus] = useState<BikeBusStatus>("pending");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (bikeBus) {
      setLeader(bikeBus.leader);
      setStatus(bikeBus.status);
    }
  }, [bikeBus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!leader.trim()) {
      toast.error("Group leader is required");
      return;
    }

    setIsSubmitting(true);
    try {
      await bikeBusService.updateBikeBus(bikeBus.bikeBusId, {
        leader: leader.trim(),
        status
      });
      
      toast.success("Bike bus updated successfully!");
      onSuccess();
    } catch (error) {
      console.error("Error updating bike bus:", error);
      toast.error("Failed to update bike bus");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusOptions: { value: BikeBusStatus; label: string }[] = [
    { value: "pending", label: "pending" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Bike Bus</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="leader">Group Leader *</Label>
            <Input
              id="leader"
              value={leader}
              onChange={(e) => setLeader(e.target.value)}
              placeholder="Enter group leader name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status *</Label>
            <Select value={status} onValueChange={(value: BikeBusStatus) => setStatus(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-muted p-3 rounded-md">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Only group leader and completion status can be edited. 
              Date and stops cannot be modified after creation.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              onClick={() => onOpenChange(false)} 
              variant="outline"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Bike Bus"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 